/**
 * Procedural textures.
 *
 * Kate asked for real life — "it looks like you're in a real jungle" — and flat-shaded
 * colour does not read as real at any distance. These build the surface detail on a
 * canvas at load time rather than shipping image files, which keeps the game a single
 * self-contained bundle with nothing to download.
 */
import * as THREE from 'three'

/** Deterministic value noise, so the same texture is generated every run. */
function makeNoise(seed: number): (x: number, y: number) => number {
  const permutation = new Uint8Array(512)
  let a = seed >>> 0
  const random = (): number => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  for (let i = 0; i < 256; i++) permutation[i] = i
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const tmp = permutation[i]!
    permutation[i] = permutation[j]!
    permutation[j] = tmp
  }
  for (let i = 0; i < 256; i++) permutation[256 + i] = permutation[i]!

  const fade = (t: number): number => t * t * (3 - 2 * t)
  const lerp = (t: number, x: number, y: number): number => x + t * (y - x)

  return (x: number, y: number): number => {
    const xi = Math.floor(x) & 255
    const yi = Math.floor(y) & 255
    const xf = x - Math.floor(x)
    const yf = y - Math.floor(y)
    const u = fade(xf)
    const v = fade(yf)
    const aa2 = permutation[permutation[xi]! + yi]!
    const ab = permutation[permutation[xi]! + yi + 1]!
    const ba = permutation[permutation[xi + 1]! + yi]!
    const bb = permutation[permutation[xi + 1]! + yi + 1]!
    return lerp(v, lerp(u, aa2 / 255, ba / 255), lerp(u, ab / 255, bb / 255))
  }
}

/** Sum several octaves so the result has both broad shape and fine grain. */
function fbm(noise: (x: number, y: number) => number, x: number, y: number, octaves: number): number {
  let value = 0
  let amplitude = 0.5
  let frequency = 1
  let total = 0
  for (let i = 0; i < octaves; i++) {
    value += noise(x * frequency, y * frequency) * amplitude
    total += amplitude
    amplitude *= 0.5
    frequency *= 2
  }
  return value / total
}

function createCanvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) throw new Error('2d context unavailable')
  return [canvas, context]
}

function finish(canvas: HTMLCanvasElement, repeat: number): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.repeat.set(repeat, repeat)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 8
  return texture
}

/**
 * Derive a normal map from a heightfield by sampling its slope. Gives the ground and
 * bark real relief under the moving sun instead of reading as painted-on detail.
 */
function normalFromHeight(height: Float32Array, size: number, strength: number): THREE.CanvasTexture {
  const [canvas, context] = createCanvas(size)
  const image = context.createImageData(size, size)
  const at = (x: number, y: number): number =>
    height[((y + size) % size) * size + ((x + size) % size)]!

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength
      // Normalise (-dx, -dy, 1) into 0..255 tangent-space RGB.
      const length = Math.hypot(dx, dy, 1)
      const i = (y * size + x) * 4
      image.data[i] = ((-dx / length) * 0.5 + 0.5) * 255
      image.data[i + 1] = ((-dy / length) * 0.5 + 0.5) * 255
      image.data[i + 2] = (1 / length) * 0.5 * 255 + 127
      image.data[i + 3] = 255
    }
  }
  context.putImageData(image, 0, 0)
  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  // Normal maps need the same anisotropic filtering the colour maps get. Without it a
  // tiled surface seen at a grazing angle --- which is most of the ground, most of the
  // time --- sparkles, because each screen pixel samples one arbitrary normal instead
  // of the average of the many it actually covers.
  texture.anisotropy = 8
  return texture
}

/**
 * sRGB bytes for a colour, ready to write into a canvas.
 *
 * THREE.Color converts a hex literal from sRGB into linear working space on assignment,
 * so `colour.r * 255` writes a linear value into a canvas that three then reads back as
 * sRGB --- gamma applied twice. Every generated surface was coming out far darker than
 * its stated colour: bark authored at luma 68 was landing at 16, which is why trunks
 * rendered as near-black slabs at full noon.
 */
function srgb255(colour: THREE.Color): [number, number, number] {
  const out = colour.clone().convertLinearToSRGB()
  return [out.r * 255, out.g * 255, out.b * 255]
}

/**
 * fbm sampled on a torus, so the resulting tile joins itself on every edge.
 *
 * Plain fbm across a canvas does not repeat: the left edge has nothing to do with the
 * right. Tiled 26 times across the ground that put a hard seam at every tile boundary,
 * and one of those boundaries landed straight through the spawn clearing. Blending the
 * four wrapped samples bilinearly is the standard fix and costs four evaluations.
 */
function tileableFbm(noise: (x: number, y: number) => number,
                     u: number, v: number, fx: number, octaves: number,
                     fy = fx): number {
  const a = fbm(noise, u * fx, v * fy, octaves)
  const b = fbm(noise, (u - 1) * fx, v * fy, octaves)
  const c = fbm(noise, u * fx, (v - 1) * fy, octaves)
  const d = fbm(noise, (u - 1) * fx, (v - 1) * fy, octaves)
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v
}

export interface SurfaceTextures {
  map: THREE.CanvasTexture
  normalMap: THREE.CanvasTexture
}

/**
 * Ground: mottled patches of a base colour, with darker damp hollows and lighter dry
 * rises, plus fine grain. Works for soil, sand, and seabed by shifting the palette.
 */
export function createGroundTexture(base: number, seed = 7, repeat = 26): SurfaceTextures {
  const size = 512
  const [canvas, context] = createCanvas(size)
  const noise = makeNoise(seed)
  const image = context.createImageData(size, size)
  const height = new Float32Array(size * size)
  const colour = new THREE.Color(base)
  const [cr, cg, cb] = srgb255(colour)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const broad = tileableFbm(noise, x / size, y / size, 5, 4)
      const grain = tileableFbm(noise, x / size, y / size, 48, 3)
      // Shade multipliers were tuned against textures that were far too dark, so they
      // ran well above 1 to claw brightness back. With the gamma bug fixed that clips:
      // the ground washed out to flat poster green. Centred on 1 now, so a surface
      // renders as the colour it was given, varied rather than brightened.
      const shade = 0.82 + broad * 0.3 + (grain - 0.5) * 0.14
      const i = (y * size + x) * 4
      image.data[i] = Math.min(255, cr * shade)
      image.data[i + 1] = Math.min(255, cg * shade)
      image.data[i + 2] = Math.min(255, cb * shade)
      image.data[i + 3] = 255
      // Mostly the broad shape. Pixel-scale grain in a normal map is a sparkle
      // generator, not detail: it is finer than a screen pixel at any real distance.
      height[y * size + x] = broad * 0.88 + grain * 0.12
    }
  }
  context.putImageData(image, 0, 0)
  const map = finish(canvas, repeat)
  const normalMap = normalFromHeight(height, size, 26)
  normalMap.repeat.set(repeat, repeat)
  return { map, normalMap }
}

/** Bark: vertical fibrous strands with knots, so trunks aren't smooth cylinders. */
export function createBarkTexture(base: number, seed = 11): SurfaceTextures {
  const size = 256
  const [canvas, context] = createCanvas(size)
  const noise = makeNoise(seed)
  const image = context.createImageData(size, size)
  const height = new Float32Array(size * size)
  const colour = new THREE.Color(base)
  const [cr, cg, cb] = srgb255(colour)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Stretched vertically: bark runs along the trunk, not around it.
      // Stretched vertically: bark runs along the trunk, not around it. The frequency
      // is deliberately different per axis, which is why tileableFbm takes both.
      const strands = tileableFbm(noise, x / size, y / size, 22, 4, 2.5)
      const knots = tileableFbm(noise, x / size, y / size, 6, 3)
      const groove = Math.abs(Math.sin(strands * Math.PI * 3)) * 0.5 + 0.5
      const shade = 0.72 + groove * 0.36 + (knots - 0.5) * 0.2
      const i = (y * size + x) * 4
      image.data[i] = Math.min(255, cr * shade)
      image.data[i + 1] = Math.min(255, cg * shade)
      image.data[i + 2] = Math.min(255, cb * shade)
      image.data[i + 3] = 255
      height[y * size + x] = groove * 0.8 + knots * 0.2
    }
  }
  context.putImageData(image, 0, 0)
  const map = finish(canvas, 1)
  map.repeat.set(2, 3)
  const normalMap = normalFromHeight(height, size, 34)
  normalMap.repeat.set(2, 3)
  return { map, normalMap }
}

/** Foliage: clumped leaf mass with holes, so canopies read as leaves not billiard balls. */
export function createFoliage(base: number, seed = 23): SurfaceTextures {
  const size = 256
  const [canvas, context] = createCanvas(size)
  const noise = makeNoise(seed)
  const image = context.createImageData(size, size)
  const colour = new THREE.Color(base)
  const [cr, cg, cb] = srgb255(colour)
  const height = new Float32Array(size * size)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const clump = tileableFbm(noise, x / size, y / size, 14, 4)
      const fine = tileableFbm(noise, x / size, y / size, 40, 2)
      const shade = 0.78 + clump * 0.36 + (fine - 0.5) * 0.16
      // Leaf clumps stand proud, so the crown catches light in bunches rather than
      // shading like one smooth rubber dome.
      height[y * size + x] = clump * 0.75 + fine * 0.25
      const i = (y * size + x) * 4
      image.data[i] = Math.min(255, cr * shade)
      image.data[i + 1] = Math.min(255, cg * shade)
      image.data[i + 2] = Math.min(255, cb * shade)
      image.data[i + 3] = 255
    }
  }
  context.putImageData(image, 0, 0)
  // Tiled small: at repeat 3 one leaf clump spanned a third of a crown, so the surface
  // read as painted blotches rather than foliage.
  const repeat = 9
  const map = finish(canvas, repeat)
  const normalMap = normalFromHeight(height, size, 22)
  normalMap.repeat.set(repeat, repeat)
  return { map, normalMap }
}

/** Water: slow ripples. Gives the pond a surface rather than a flat blue disc. */
export function createWaterNormal(seed = 31): THREE.CanvasTexture {
  const size = 256
  const noise = makeNoise(seed)
  const height = new Float32Array(size * size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      height[y * size + x] = fbm(noise, (x / size) * 9, (y / size) * 9, 3)
    }
  }
  const normalMap = normalFromHeight(height, size, 14)
  normalMap.repeat.set(7, 7)
  return normalMap
}
